// src/services/logoutService.js
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '../authConfig';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost/trv/api/';

class LogoutService {
     constructor () {
          this.msalInstance = new PublicClientApplication(msalConfig);
     }

     /**
      * Complete logout - handles both Microsoft MSAL and custom API logout
      * @param {Object} options - Logout options
      * @param {boolean} options.redirectToLogout - Whether to redirect to Microsoft logout page
      * @param {boolean} options.clearAllData - Whether to clear all local storage data
      * @param {string} options.redirectUrl - Custom redirect URL after logout
      * @returns {Promise<boolean>} - Success status
      */
     async performCompleteLogout(options = {}) {
          const {
               redirectToLogout = true,
               clearAllData = true,
               redirectUrl = window.location.origin
          } = options;

          try {
               console.log('Starting logout process...');

               // Step 1: Logout from custom API if token exists
               await this.logoutFromAPI();

               // Step 2: Clear all local storage and session storage
               if (clearAllData) {
                    this.clearAllStorageData();
               }

               // Step 3: Logout from Microsoft MSAL
               await this.logoutFromMicrosoft(redirectToLogout, redirectUrl);

               // Step 4: Additional cleanup
               this.performAdditionalCleanup();

               console.log('Logout process completed successfully');
               return true;

          } catch (error) {
               console.error('Error during logout process:', error);
               // Even if logout fails, clear local data for security
               if (clearAllData) {
                    this.clearAllStorageData();
               }
               throw error;
          }
     }

     /**
      * Logout from custom API backend
      */
     async logoutFromAPI() {
          try {
               const token = localStorage.getItem('authToken');
               if (!token) {
                    console.log('No auth token found, skipping API logout');
                    return;
               }

               console.log('Logging out from API...');
               const response = await fetch(`${API_BASE_URL}logout`, {
                    method: 'POST',
                    headers: {
                         'Authorization': `Bearer ${token}`,
                         'Content-Type': 'application/json'
                    },
                    timeout: 5000 // 5 second timeout
               });

               if (response.ok) {
                    const result = await response.json();
                    console.log('API logout response:', result);
               } else {
                    console.warn('API logout failed with status:', response.status);
               }
          } catch (error) {
               console.warn('API logout error (continuing with logout):', error);
               // Don't throw error here - continue with logout process
          }
     }

     /**
      * Logout from Microsoft MSAL
      */
     async logoutFromMicrosoft(redirectToLogout = true, redirectUrl = window.location.origin) {
          try {
               const accounts = this.msalInstance.getAllAccounts();

               if (accounts.length === 0) {
                    console.log('No Microsoft accounts found, skipping MSAL logout');
                    return;
               }

               console.log('Logging out from Microsoft...');

               if (redirectToLogout) {
                    // Redirect logout - user will be redirected to Microsoft logout page
                    const logoutRequest = {
                         account: accounts[0],
                         postLogoutRedirectUri: redirectUrl,
                         onRedirectNavigate: () => {
                              return false; // Don't prevent redirect
                         }
                    };

                    await this.msalInstance.logoutRedirect(logoutRequest);
               } else {
                    // Silent logout - no redirect
                    const logoutRequest = {
                         account: accounts[0]
                    };

                    await this.msalInstance.logoutSilent(logoutRequest);
               }
          } catch (error) {
               console.warn('Microsoft logout error:', error);
               // Continue with cleanup even if MSAL logout fails
          }
     }

     /**
      * Clear all storage data
      */
     clearAllStorageData() {
          console.log('Clearing storage data...');

          // Clear specific auth-related items
          const authKeys = [
               'authToken',
               'authToken1',
               'exp',
               'userProfile',
               'refreshToken',
               'sessionId',
               'lastActivity'
          ];

          authKeys.forEach(key => {
               localStorage.removeItem(key);
               sessionStorage.removeItem(key);
          });

          // Clear MSAL cache
          try {
               this.msalInstance.clearCache();
          } catch (error) {
               console.warn('Error clearing MSAL cache:', error);
          }

          // Optional: Clear all localStorage/sessionStorage (use with caution)
          // localStorage.clear();
          // sessionStorage.clear();
     }

     /**
      * Perform additional cleanup
      */
     performAdditionalCleanup() {
          // Clear any timers or intervals
          if (window.sessionTimer) {
               clearInterval(window.sessionTimer);
               delete window.sessionTimer;
          }

          // Clear any other application-specific data
          if (window.applicationState) {
               delete window.applicationState;
          }

          // Clear service worker cache if needed
          if ('serviceWorker' in navigator) {
               navigator.serviceWorker.getRegistrations().then(registrations => {
                    registrations.forEach(registration => {
                         registration.unregister();
                    });
               });
          }
     }

     /**
      * Quick logout - minimal cleanup, fast execution
      */
     async performQuickLogout() {
          console.log('Performing quick logout...');

          // Clear essential data only
          localStorage.removeItem('authToken');
          sessionStorage.removeItem('authToken1');

          // Navigate to login page immediately
          window.location.href = '/';
     }

     /**
      * Emergency logout - for security issues
      */
     async performEmergencyLogout() {
          console.log('Performing emergency logout...');

          // Clear everything immediately
          localStorage.clear();
          sessionStorage.clear();

          // Clear cookies
          this.clearAllCookies();

          // Force reload to login page
          window.location.replace('/');
     }

     /**
      * Clear all cookies
      */
     clearAllCookies() {
          document.cookie.split(";").forEach(cookie => {
               const eqPos = cookie.indexOf("=");
               const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
               document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
               document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
          });
     }

     /**
      * Check if user is authenticated
      */
     isAuthenticated() {
          const token = localStorage.getItem('authToken');
          const msalAccounts = this.msalInstance.getAllAccounts();

          return !!(token || msalAccounts.length > 0);
     }
}

// Export singleton instance
export const logoutService = new LogoutService();
export default LogoutService;
